/**
 * AdminAgentDashboard - World-Class Minimalist Design
 * AI Agent management with elegant, modern UI
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, Users, DollarSign, Activity, Loader2,
  Bot, FileText, Shield, Clock, CheckCircle2, Zap, Brain
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Agent configurations with icons and colors
const AGENT_CONFIG: Record<string, { name: string; nameEn: string; icon: any; color: string; bgColor: string; gradient: string }> = {
  DOCUMENT: { 
    name: 'DokumentAgent', 
    nameEn: 'Document Agent',
    icon: FileText, 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-50',
    gradient: 'from-blue-500 to-blue-600'
  },
  RISK: { 
    name: 'RisikoAgent', 
    nameEn: 'Risk Agent',
    icon: Shield, 
    color: 'text-amber-600', 
    bgColor: 'bg-amber-50',
    gradient: 'from-amber-500 to-orange-500'
  },
  TASK: { 
    name: 'OppgaveAgent', 
    nameEn: 'Task Agent',
    icon: CheckCircle2, 
    color: 'text-emerald-600', 
    bgColor: 'bg-emerald-50',
    gradient: 'from-emerald-500 to-teal-500'
  },
  TIMELINE: { 
    name: 'TidslinjeAgent', 
    nameEn: 'Timeline Agent',
    icon: Clock, 
    color: 'text-violet-600', 
    bgColor: 'bg-violet-50',
    gradient: 'from-violet-500 to-purple-500'
  },
  MEETING: { 
    name: 'MøteAgent', 
    nameEn: 'Meeting Agent',
    icon: Users, 
    color: 'text-rose-600', 
    bgColor: 'bg-rose-50',
    gradient: 'from-rose-500 to-pink-500'
  }
};

const translations = {
  en: {
    title: 'AI Agents',
    subtitle: 'Monitor and manage your AI assistant fleet',
    activeCases: 'Active Cases',
    activeUsers: 'Active Users',
    agentRuns: 'Agent Runs',
    aiCost: 'AI Cost',
    thisMonth: 'This month',
    agentStats: 'Agent Performance',
    last30Days: 'Last 30 days',
    runs: 'runs',
    suggestions: 'Suggestions',
    approved: 'Approved',
    quality: 'Quality',
    cost: 'Cost',
    avgTime: 'Avg Time',
    viewAll: 'View all agents'
  },
  no: {
    title: 'AI Agenter',
    subtitle: 'Overvåk og administrer AI-assistentene dine',
    activeCases: 'Aktive saker',
    activeUsers: 'Aktive brukere',
    agentRuns: 'Agent-kjøringer',
    aiCost: 'AI kostnad',
    thisMonth: 'Denne måneden',
    agentStats: 'Agent-ytelse',
    last30Days: 'Siste 30 dager',
    runs: 'kjøringer',
    suggestions: 'Forslag',
    approved: 'Godkjent',
    quality: 'Kvalitet',
    cost: 'Kostnad',
    avgTime: 'Gj.snitt tid',
    viewAll: 'Se alle agenter'
  }
};

interface AdminAgentDashboardProps {
  language?: string;
}

const AdminAgentDashboard: React.FC<AdminAgentDashboardProps> = ({ language = 'en' }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [agentStats, setAgentStats] = useState<any[]>([]);

  const t = translations[language] || translations.en;
  const isEnglish = language === 'en';

  // Mock data for demo
  const mockOverview = {
    active_cases: 24,
    active_users: 8,
    agent_runs_this_month: 156,
    agent_runs_change: '+23%',
    total_cost_this_month: 45.20,
    cost_change: '+$12'
  };

  const mockAgentStats = [
    { agent_type: 'DOCUMENT', runs: 67, suggestions: 234, approved: 198, approval_rate: 85, total_cost: 18.50, avg_duration_seconds: 2.3 },
    { agent_type: 'RISK', runs: 45, suggestions: 89, approved: 71, approval_rate: 80, total_cost: 12.30, avg_duration_seconds: 3.1 },
    { agent_type: 'TASK', runs: 34, suggestions: 156, approved: 142, approval_rate: 91, total_cost: 8.40, avg_duration_seconds: 1.8 },
    { agent_type: 'TIMELINE', runs: 10, suggestions: 28, approved: 22, approval_rate: 79, total_cost: 6.00, avg_duration_seconds: 4.2 },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, agentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats/agent-overview`, { headers }).catch(() => null),
        axios.get(`${API}/admin/stats/agents?days=30`, { headers }).catch(() => null),
      ]);

      if (overviewRes?.data) setOverview(overviewRes.data);
      if (agentsRes?.data?.agents) setAgentStats(agentsRes.data.agents);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const displayOverview = overview || mockOverview;
  const displayAgentStats = agentStats.length > 0 ? agentStats : mockAgentStats;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          <span className="text-sm text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="ai-agents-dashboard">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-200">
            <Brain className="w-5 h-5 text-white" />
          </div>
          {t.title}
        </h2>
        <p className="text-gray-500 text-sm mt-2">{t.subtitle}</p>
      </div>

      {/* KPI Cards - Bento Grid Style */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: t.activeCases, value: displayOverview.active_cases, icon: Activity, color: 'violet', gradient: 'from-violet-500 to-purple-600' },
          { label: t.activeUsers, value: displayOverview.active_users, icon: Users, color: 'blue', gradient: 'from-blue-500 to-indigo-600' },
          { label: t.agentRuns, value: displayOverview.agent_runs_this_month, change: displayOverview.agent_runs_change, icon: Zap, color: 'emerald', gradient: 'from-emerald-500 to-teal-600' },
          { label: t.aiCost, value: `$${displayOverview.total_cost_this_month}`, change: displayOverview.cost_change, icon: DollarSign, color: 'amber', gradient: 'from-amber-500 to-orange-500' },
        ].map((kpi, i) => (
          <motion.div
            key={kpi.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg hover:border-gray-200 transition-all group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${kpi.gradient} flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
                <kpi.icon className="w-5 h-5 text-white" />
              </div>
              {kpi.change && (
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                  {kpi.change}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-1">{kpi.label}</p>
            <p className="text-3xl font-semibold text-gray-900">{kpi.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Agent Cards Grid */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-gray-900">{t.agentStats}</h3>
          <span className="text-sm text-gray-400">{t.last30Days}</span>
        </div>
        
        <div className="grid grid-cols-2 gap-5">
          {displayAgentStats.map((stat, index) => {
            const config = AGENT_CONFIG[stat.agent_type] || { 
              name: stat.agent_type, 
              nameEn: stat.agent_type,
              icon: Bot, 
              color: 'text-gray-600', 
              bgColor: 'bg-gray-50',
              gradient: 'from-gray-500 to-gray-600'
            };
            const IconComponent = config.icon;
            
            return (
              <motion.div
                key={stat.agent_type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all group"
              >
                {/* Gradient header bar */}
                <div className={`h-1 bg-gradient-to-r ${config.gradient}`} />
                
                <div className="p-5">
                  {/* Agent header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-11 h-11 rounded-xl ${config.bgColor} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                      <IconComponent className={`w-5 h-5 ${config.color}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {isEnglish ? config.nameEn : config.name}
                      </h4>
                      <p className="text-xs text-gray-400">{stat.runs} {t.runs}</p>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-4 mb-5">
                    <div className="text-center p-3 bg-gray-50 rounded-xl">
                      <p className="text-xl font-semibold text-gray-900">{stat.suggestions}</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">{t.suggestions}</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-xl">
                      <p className="text-xl font-semibold text-emerald-600">{stat.approved}</p>
                      <p className="text-[10px] text-emerald-600 uppercase tracking-wider mt-1">{t.approved}</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-xl">
                      <p className="text-xl font-semibold text-gray-900">${stat.total_cost}</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">{t.cost}</p>
                    </div>
                  </div>

                  {/* Quality bar */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">{t.quality}</span>
                      <span className="text-sm font-semibold text-gray-900">{stat.approval_rate}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${stat.approval_rate}%` }}
                        transition={{ duration: 0.8, delay: 0.4 + index * 0.1 }}
                        className={`h-full rounded-full bg-gradient-to-r ${config.gradient}`}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminAgentDashboard;
